import React, { useRef } from 'react';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import { useTranslation } from 'react-i18next';
import {
  BreadcrumbChild,
  BreadScrumTitle,
} from '@/components/Approval/Content/DocumentContents/styles';
import WorkContentLayout from '@/components/Approval/@components/WorkContentLayout';
import ApprovalHeader from '@/components/Approval/@components/ApprovalHeader';
import useHandleScroll from '@/components/Approval/Content/hooks/useHandleScroll';
import { SearchData } from '@/types/api';
import { FormProvider, useForm } from 'react-hook-form';
import PaginationBar from '@/components/PaginationBar';
import DocumentFilterBar from '@/components/Approval/Content/DocumentContents/DocumentFilterBar';
import DocumentSort from '@/components/Approval/Content/DocumentContents/DocumentSort';
import { ListContainer } from '@/components/Approval/RightContainer/styles';
import NoData from '@/components/Approval/@components/NoData';
import Loading from '@/components/Approval/RightContainer/ApprovalContent/Loading';
import TalkFLowModal, {
  TalkFlowModalRef,
} from '@/components/Record/TalkFLowModal';
import NoReadButton from '@/components/Approval/@components/NoReadButton';
import useDocumentsData from '@/components/Approval/Content/hooks/useDocumentsData';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { EventPageAreaName } from '@/constant/gtm';
import { FilterList } from '@/components/Approval/Content/DocumentFilters';
import { DashIcon } from '@/theme/overrides/CustomIcons';
import { useTheme, useMediaQuery } from '@mui/material';
import StackWithDivider from '@/components/StackWithDivider';
import DocumentItem from './DocumentItem';
import useQueuedRecordDocIds from '@/hooks/useQueuedRecordDocIds';
import RecordingDuration from './RecordingDuration';

export const DocumentTitle = 'Meeting Notes';

const DocumentContents = () => {
  const pageArea = EventPageAreaName.WORK_DOCUMENT;
  const workEventTracker = useWorkEventTracker(pageArea);
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  // 이 기기에 업로드하지 못한 녹음이 남은 문서 — 「업로드 실패」 라벨 (PORTAL-12583)
  const queuedRecordDocIds = useQueuedRecordDocIds();
  const [filters, setFilters] = useAtom(documentFilterState);
  const { showButton, isTablet, scrollToTop } = useHandleScroll(ref);
  const isAllCategory = filters.category === 'all';
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl')); // 1440px 이상

  const methods = useForm<SearchData>({
    defaultValues: { keyword: '', searchFields: 'subject,content' },
  });

  const handleClickPage = (num: number) => {
    setFilters({ ...filters, currentPage: num });
  };
  const { handleBookmark, contentMutate, data, isLoading, disabled } =
    useDocumentsData();
  const documentModal = useRef<TalkFlowModalRef>(null);

  const onAfterClose = () => {
    contentMutate();
  };

  const handleChange = () => {
    if (filters.isRead === null) {
      setFilters({ ...filters, isRead: false });
    } else {
      setFilters({ ...filters, isRead: null });
    }
  };

  return (
    <WorkContentLayout
      ref={ref}
      showTopButton={showButton}
      onScrollToTop={scrollToTop}
    >
      <RecordingDuration />
      <ApprovalHeader
        breadcrumb={
          <>
            <BreadScrumTitle>{DocumentTitle}</BreadScrumTitle>
            <DashIcon style={{ margin: '8px' }} />
            <BreadcrumbChild>
              {FilterList.find((res) => res.value === filters.category)?.title}
            </BreadcrumbChild>
          </>
        }
        totalCount={data?.meta.totalCount}
        keyword={filters.keyword}
        leftButton={
          filters.category === 'referrer' && (
            <NoReadButton
              title={t('안읽은 공유 문서만 조회')}
              isChecked={filters.isRead === false}
              onChange={handleChange}
            />
          )
        }
        filterComponent={
          <FormProvider {...methods}>
            <DocumentFilterBar isTablet={isTablet} />
          </FormProvider>
        }
        searchComponent={<DocumentSort />}
        isTablet={isTablet}
      />

      <ListContainer>
        {!isLoading ? (
          data && data.data.length > 0 ? (
            data.data.map((item) => (
              <StackWithDivider key={item.id}>
                <DocumentItem
                  item={item}
                  isDesktop={isDesktop}
                  disabled={disabled}
                  onDocumentClick={(id) => documentModal.current?.open(id)}
                  onBookmark={handleBookmark}
                  workEventTracker={workEventTracker}
                  uploadFailed={queuedRecordDocIds.has(item.id)}
                />
              </StackWithDivider>
            ))
          ) : (
            <NoData
              title={
                isAllCategory && (
                  <>
                    {t('아직 문서가 없어요.')}
                    <br />
                    {t('문서를 작성하거나 공유받으면 이곳에 표시돼요.')}
                  </>
                )
              }
            />
          )
        ) : (
          <Loading />
        )}
        {data?.meta.endPage && (
          <PaginationBar
            isFixed={false}
            isSticky={true}
            currentPage={filters.currentPage}
            totalPage={data?.meta.endPage}
            handleClick={(num: number) => {
              handleClickPage(num);
              scrollToTop();
            }}
          />
        )}
      </ListContainer>

      <TalkFLowModal
        ref={documentModal}
        onAfterClose={onAfterClose}
        pageArea={pageArea}
      />
    </WorkContentLayout>
  );
};
export default DocumentContents;
